import {
  type Variant,
  type PurchaseOption,
  type NFFVariantMeta,
  PaymentOption,
} from '../../../types/CoreShoppingExperience';
import type Currency from '../../../types/payment/Currency';
import type { ShoppingCartItem } from '../../../types/shoppingCart/ShoppingCart';
import findFallBackProduct from '../../libs/utils/products/findFallBackProduct';
import findProduct from '../../libs/utils/products/findProduct';
import useExperience from '../experience/useExperience';

export interface UseConvertVariantToShoppingCartItemOptions {
  amount: number;
  pointsAmount?: number;
  purchaseOption: PurchaseOption;
  pointsPurchaseOption?: PurchaseOption; // passed in mixed currency
  currency: Currency;
  equipment: string;
  marketingProductNumber?: number;
  /* When enabled, the quantity will be ignored when calculating the total amount.
    This flag is used for some verticals that have the total amount coming from the backend as the final price,
    as they don't need to use the quantity to multiply the total amount. */
  isTotalAmountCalculatedInBackend?: boolean;
  meta?: NFFVariantMeta;
  roundTripBundle?: boolean;
  isFallBack?: boolean;
}

const useConvertVariantToShoppingCartItem = () => {
  const { experience } = useExperience().states;

  const convertVariantToShoppingCartItem = (
    variant: Variant,
    {
      amount,
      pointsAmount,
      currency,
      purchaseOption,
      pointsPurchaseOption,
      equipment,
      marketingProductNumber,
      isTotalAmountCalculatedInBackend,
      meta,
      roundTripBundle,
      isFallBack = false,
    }: UseConvertVariantToShoppingCartItemOptions,
  ): ShoppingCartItem => {
    if (!experience) throw new Error('impossible to convert since experience is undefined');

    const product = isFallBack
      ? findFallBackProduct(experience, { variantEncryptedId: variant.encryptedId })
      : findProduct(experience, { variantEncryptedId: variant.encryptedId });

    if (!product) throw new Error('impossible to find product');
    const shoppingCartItem: ShoppingCartItem = {
      encryptedId: variant.encryptedId,
      upgradeType: variant.upgradeType,
      textkeyGroup: variant.textKey,
      quantity: purchaseOption.quantity.quantity,
      quantityType: purchaseOption.quantity.type,
      fromCabin: variant.fromCabin,
      variantFromCabin: variant.fromCabin,
      equipment,
      amount,
      pointsAmount,
      encryptedProductId: product.encryptedId,
      departureDate: product.productDetails.departureDate,
      departureDateTime: product.productDetails.departureDateTime,
      durationInMinutes: product.productDetails.durationInMinutes,
      originCode: product.productDetails.originCode,
      destinationCode: product.productDetails.destinationCode,
      pointOfSale: experience?.booking.pointOfSale ?? '',
      bookingNumber: experience?.booking.bookingNumber ?? '',
      encryptedExperienceContext: experience?.encryptedExperienceContext ?? '',
      currency,
      marketingProductNumber,
      isTotalAmountCalculatedInBackend,
      ...(meta && { meta }),
      ...(roundTripBundle && { roundTripBundle }),
      ...(isFallBack && { isFallBack }),
    };
    if (pointsPurchaseOption) {
      // we are in mixed currency so lets assign purchaseOption to cash and the other to points
      shoppingCartItem.cashPurchaseOption = purchaseOption;
      shoppingCartItem.pointsPurchaseOption = pointsPurchaseOption;
    } else if (purchaseOption.paymentOption === PaymentOption.CASH) {
      shoppingCartItem.cashPurchaseOption = purchaseOption;
    } else if (purchaseOption.paymentOption === PaymentOption.POINTS) {
      shoppingCartItem.pointsPurchaseOption = purchaseOption;
    }
    return shoppingCartItem;
  };

  return {
    actions: {
      convertVariantToShoppingCartItem,
    },
  };
};

export default useConvertVariantToShoppingCartItem;
